(function (window, document) {
  'use strict';

  const PRINT_SPACING = 0.25;
  const STANDARD_ALLOWANCE = 0.5;
  const CONTOUR_ALLOWANCE = 2;
  const MINIMUM_SQUARE_FEET = 4;

  function formatNumber(value, decimals = 2) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '';
    return Number(number.toFixed(decimals)).toString();
  }

  function normalize(value) {
    return String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function isContourCutValue(value) {
    const text = normalize(value);

    if (!text) return false;
    if (text.includes('no contour cut')) return false;

    return (
      text.includes('contour cut') ||
      text === 'yes'
    );
  }

  function readMediaSizes(rawValue) {
    const widths = [];

    function extract(value) {
      if (typeof value === 'number') {
        if (value > 0 && value <= 200) widths.push(value);
        return;
      }

      if (typeof value === 'string') {
        const matches = value.match(/\d+(?:\.\d+)?/g) || [];
        matches.forEach((match) => {
          const number = parseFloat(match);
          if (number > 0 && number <= 200) widths.push(number);
        });
        return;
      }

      if (Array.isArray(value)) {
        value.forEach(extract);
        return;
      }

      if (value && typeof value === 'object') {
        Object.values(value).forEach(extract);
      }
    }

    extract(rawValue);

    return [...new Set(widths)].sort((a, b) => a - b);
  }

  function initCalculator(calc) {
    if (!calc || calc.dataset.awReady === 'true') return;
    calc.dataset.awReady = 'true';

    let data = {};
    try {
      data = JSON.parse(calc.querySelector('[data-aw-calculator-data]')?.textContent || '{}');
    } catch (error) {
      console.warn('Unable to parse Alwan calculator data.', error);
    }

    function getProductForm() {
      const formId = calc.dataset.awProductFormId;

      return (
        (formId ? document.getElementById(formId) : null) ||
        calc.closest('form[action*="/cart/add"]') ||
        document.querySelector('form[action*="/cart/add"]')
      );
    }

    function getCurrentVariantId() {
      const form = getProductForm();

      const checked = form?.querySelector('input[name="id"]:checked');
      if (checked?.value) return String(checked.value);

      const select = form?.querySelector('select[name="id"]');
      if (select?.value) return String(select.value);

      const hidden = form?.querySelector('input[name="id"]');
      if (hidden?.value) return String(hidden.value);

      try {
        const urlVariant = new URL(window.location.href).searchParams.get('variant');
        if (urlVariant) return String(urlVariant);
      } catch (error) {}

      return data.selectedVariantId ? String(data.selectedVariantId) : '';
    }

    function getSelectedCuttingFinishingValue() {
      const variantId = getCurrentVariantId();
      const variant = data.variants?.[variantId];

      return variant?.cuttingFinishing || '';
    }

    function contourCutSelected() {
      return isContourCutValue(getSelectedCuttingFinishingValue());
    }

    function getAllowance() {
      return contourCutSelected() ? CONTOUR_ALLOWANCE : STANDARD_ALLOWANCE;
    }

    function getUsableWidth(mediaWidth) {
      return Math.max(0, Number(mediaWidth) - getAllowance());
    }

    const mediaWidths = readMediaSizes(data.mediaSizes);
    const mediaLength = parseFloat(data.mediaLength) || 0;

    const widthInput = calc.querySelector('[data-aw-width]');
    const heightInput = calc.querySelector('[data-aw-height]');
    const sqftInput = calc.querySelector('[data-aw-sqft]');
    const mediaWidthSelect = calc.querySelector('[data-aw-media-width]');

    const dimensionCopies = calc.querySelector('[data-aw-dimension-copies]');
    const singleRadio = calc.querySelector('[data-aw-copy-single]');
    const fillRadio = calc.querySelector('[data-aw-copy-fill]');
    const fillRow = calc.querySelector('[data-aw-copy-fill-row]');
    const fillDescription = calc.querySelector('[data-aw-copy-fill-description]');
    const quantityRadio = calc.querySelector('[data-aw-copy-quantity-radio]');
    const quantityWrap = calc.querySelector('[data-aw-copy-quantity-container]');
    const quantityInput = calc.querySelector('[data-aw-copy-quantity]');

    const sqftSingleRadio = calc.querySelector('[data-aw-sqft-copy-single]');
    const sqftQuantityRadio = calc.querySelector('[data-aw-sqft-copy-quantity-radio]');
    const sqftQuantityWrap = calc.querySelector('[data-aw-sqft-copy-quantity-container]');
    const sqftQuantityInput = calc.querySelector('[data-aw-sqft-copy-quantity]');

    const propWidth = calc.querySelector('[data-aw-property-width]');
    const propHeight = calc.querySelector('[data-aw-property-height]');
    const propSqft = calc.querySelector('[data-aw-property-sqft]');
    const propMedia = calc.querySelector('[data-aw-property-media-width]');
    const propCopies = calc.querySelector('[data-aw-property-copies]');

    const result = calc.querySelector('[data-aw-result]');
    const resultSqft = calc.querySelector('[data-aw-result-sqft]');
    const resultMedia = calc.querySelector('[data-aw-result-media]');
    const resultMax = calc.querySelector('[data-aw-result-max]');
    const message = calc.querySelector('[data-aw-message]');
    const secondary = calc.querySelector('[data-aw-secondary]');

    let activeMode = 'dimensions';
    let calculatorValid = false;
    let currentSquareFeet = 0;

    function setProperty(input, value) {
      if (input) input.value = value === null || value === undefined ? '' : String(value);
    }

    function publish(squareFeet = currentSquareFeet) {
      document.dispatchEvent(new CustomEvent('alwan:calculator:update', {
        detail: {
          calculator: calc,
          mode: activeMode,
          valid: calculatorValid,
          squareFeet: squareFeet || 0,
          contourCut: contourCutSelected(),
          allowance: getAllowance()
        }
      }));
    }

    function setMessage(text) {
      if (!message) return;
      message.textContent = text || '';
      message.classList.toggle('is-visible', !!text);
    }

    function setSecondary(text) {
      if (!secondary) return;
      secondary.textContent = text || '';
      secondary.classList.toggle('is-visible', !!text);
    }

    function hideResult() {
      result?.classList.remove('is-visible');
      if (resultSqft) resultSqft.textContent = '—';
      if (resultMedia) resultMedia.textContent = '—';
      if (resultMax) resultMax.textContent = 'Maximum print area: —';
    }

    function showResult(squareFeet, mediaWidth) {
      if (!squareFeet || !mediaWidth) {
        hideResult();
        return;
      }

      if (resultSqft) resultSqft.textContent = formatNumber(squareFeet);
      if (resultMedia) resultMedia.textContent = `${formatNumber(mediaWidth)}″`;

      if (resultMax) {
        const usable = getUsableWidth(mediaWidth);
        resultMax.textContent = mediaLength
          ? `Maximum print area: ${formatNumber(usable)}″ × ${formatNumber(mediaLength)}″`
          : `Maximum printable width: ${formatNumber(usable)}″`;
      }

      result?.classList.add('is-visible');
    }

    function clearCalculatedState() {
      calculatorValid = false;
      currentSquareFeet = 0;
      setProperty(propSqft, '');
      setProperty(propMedia, '');
      setProperty(propCopies, 1);
      hideResult();
      publish(0);
    }

    function getNumericQuantity(input) {
      const quantity = parseInt(input?.value, 10);
      return Number.isFinite(quantity) ? Math.max(2, quantity) : 2;
    }

    function normalizeQuantity(input) {
      const quantity = getNumericQuantity(input);
      if (input) input.value = quantity;
      return quantity;
    }

    function getDimensionCopyMode() {
      if (quantityRadio?.checked) return 'quantity';
      if (fillRadio?.checked) return 'fill';
      return 'single';
    }

    function getDimensionCopies() {
      const mode = getDimensionCopyMode();
      if (mode === 'quantity') return getNumericQuantity(quantityInput);
      if (mode === 'fill') return Math.max(1, parseInt(fillRadio?.dataset.maxCopies, 10) || 1);
      return 1;
    }

    function getSqftCopies() {
      return sqftQuantityRadio?.checked ? getNumericQuantity(sqftQuantityInput) : 1;
    }

    function updateCopyVisibility() {
      const width = parseFloat(widthInput?.value);
      const height = parseFloat(heightInput?.value);

      if (dimensionCopies) dimensionCopies.hidden = !(width > 0 && height > 0);
      if (quantityWrap) quantityWrap.hidden = !quantityRadio?.checked;
      if (sqftQuantityWrap) sqftQuantityWrap.hidden = !sqftQuantityRadio?.checked;
    }

    function getCopiesAcross(across, mediaWidth) {
      const usableWidth = getUsableWidth(mediaWidth);

      if (across <= 0 || usableWidth <= 0 || across > usableWidth) return 0;

      return Math.floor(
        (usableWidth + PRINT_SPACING) /
        (across + PRINT_SPACING)
      );
    }

    function buildLayout(across, down, mediaWidth, quantity) {
      const copiesAcross = getCopiesAcross(across, mediaWidth);
      if (copiesAcross < 1) return null;

      const rows = Math.ceil(quantity / copiesAcross);
      const rollLength =
        (rows * down) +
        (Math.max(0, rows - 1) * PRINT_SPACING);

      if (mediaLength > 0 && rollLength > mediaLength) return null;

      const rawSquareFeet = (mediaWidth * rollLength) / 144;
      const squareFeet = Math.max(MINIMUM_SQUARE_FEET, Math.round(rawSquareFeet));

      return {
        mediaWidth,
        across,
        down,
        copiesAcross,
        rows,
        rollLength,
        rawSquareFeet,
        squareFeet,
        isMinimum: rawSquareFeet < MINIMUM_SQUARE_FEET
      };
    }

    function bestLayoutForMedia(width, height, mediaWidth, quantity) {
      const layouts = [];

      const normal = buildLayout(width, height, mediaWidth, quantity);
      if (normal) layouts.push(normal);

      if (width !== height) {
        const rotated = buildLayout(height, width, mediaWidth, quantity);
        if (rotated) layouts.push(rotated);
      }

      layouts.sort((a, b) => {
        if (a.squareFeet !== b.squareFeet) return a.squareFeet - b.squareFeet;
        if (Math.abs(a.rawSquareFeet - b.rawSquareFeet) > 0.001) {
          return a.rawSquareFeet - b.rawSquareFeet;
        }
        return b.copiesAcross - a.copiesAcross;
      });

      return layouts[0] || null;
    }

    function calculateBestLayout(width, height, quantity) {
      const layouts = mediaWidths
        .map((mediaWidth) => bestLayoutForMedia(width, height, mediaWidth, quantity))
        .filter(Boolean);

      layouts.sort((a, b) => {
        if (a.squareFeet !== b.squareFeet) return a.squareFeet - b.squareFeet;
        if (Math.abs(a.rawSquareFeet - b.rawSquareFeet) > 0.001) {
          return a.rawSquareFeet - b.rawSquareFeet;
        }
        return a.mediaWidth - b.mediaWidth;
      });

      return layouts[0] || null;
    }

    function getMaxCopiesForPurchasedMedia(layout, width, height) {
      if (!layout) return 1;

      const usableWidth = getUsableWidth(layout.mediaWidth);
      const billableLength = (layout.squareFeet * 144) / layout.mediaWidth;

      let availableLength = Math.max(layout.rollLength, billableLength);
      if (mediaLength > 0) availableLength = Math.min(availableLength, mediaLength);

      const orientations = [
        { across: width, down: height },
        { across: height, down: width }
      ];

      let maxCopies = 1;

      orientations.forEach((orientation) => {
        if (
          orientation.across > usableWidth ||
          orientation.down > availableLength
        ) return;

        const columns = Math.floor(
          (usableWidth + PRINT_SPACING) /
          (orientation.across + PRINT_SPACING)
        );

        const rows = Math.floor(
          (availableLength + PRINT_SPACING) /
          (orientation.down + PRINT_SPACING)
        );

        maxCopies = Math.max(maxCopies, columns * rows);
      });

      return maxCopies;
    }

    function updateFillAvailability(singleLayout, width, height) {
      const maxCopies = getMaxCopiesForPurchasedMedia(singleLayout, width, height);

      if (!singleLayout || maxCopies < 2) {
        if (fillRow) fillRow.hidden = true;
        if (fillRadio) fillRadio.dataset.maxCopies = '';
        if (fillDescription) fillDescription.textContent = '';

        if (fillRadio?.checked) {
          fillRadio.checked = false;
          if (singleRadio) singleRadio.checked = true;
        }
        return;
      }

      if (fillRow) fillRow.hidden = false;
      if (fillRadio) fillRadio.dataset.maxCopies = String(maxCopies);

      if (fillDescription) {
        fillDescription.textContent = singleLayout.isMinimum
          ? `Approx. ${maxCopies} copies total within the same ${singleLayout.squareFeet} sq ft minimum.`
          : `Approx. ${maxCopies} copies total within the same ${singleLayout.squareFeet} sq ft.`;
      }
    }

    function recalculateDimensions() {
      if (activeMode !== 'dimensions') return;

      const width = parseFloat(widthInput?.value) || 0;
      const height = parseFloat(heightInput?.value) || 0;

      setProperty(propWidth, width > 0 ? formatNumber(width) : '');
      setProperty(propHeight, height > 0 ? formatNumber(height) : '');
      updateCopyVisibility();

      if (width <= 0 || height <= 0) {
        if (fillRow) fillRow.hidden = true;
        setMessage('');
        setSecondary('');
        clearCalculatedState();
        return;
      }

      const singleLayout = calculateBestLayout(width, height, 1);

      if (!singleLayout) {
        if (fillRow) fillRow.hidden = true;
        setSecondary('');
        clearCalculatedState();

        const maxMedia = mediaWidths.length ? Math.max(...mediaWidths) : 0;
        const maxPrintable = maxMedia ? getUsableWidth(maxMedia) : 0;

        setMessage(
          mediaLength > 0
            ? `No suitable media found. Maximum usable width is ${formatNumber(maxPrintable)}″ and maximum roll length is ${formatNumber(mediaLength)}″.`
            : `No suitable media found. Maximum usable width is ${formatNumber(maxPrintable)}″.`
        );
        return;
      }

      updateFillAvailability(singleLayout, width, height);

      const copyMode = getDimensionCopyMode();
      const copies = getDimensionCopies();
      let activeLayout = singleLayout;

      if (copyMode === 'quantity') {
        activeLayout = calculateBestLayout(width, height, copies);

        if (!activeLayout) {
          setSecondary('');
          clearCalculatedState();
          setProperty(propCopies, copies);
          setMessage(
            mediaLength > 0
              ? `The selected quantity exceeds the available printable media length of ${formatNumber(mediaLength)}″.`
              : 'The selected quantity exceeds the available media.'
          );
          return;
        }
      }

      calculatorValid = true;
      currentSquareFeet = activeLayout.squareFeet;

      setProperty(propSqft, currentSquareFeet);
      setProperty(propMedia, `${formatNumber(activeLayout.mediaWidth)}in`);
      setProperty(propCopies, copies);

      showResult(currentSquareFeet, activeLayout.mediaWidth);
      setMessage('');
      setSecondary('');

      publish(currentSquareFeet);
    }

    function recalculateSquareFootage() {
      if (activeMode !== 'squareFootage') return;

      const requested = parseFloat(sqftInput?.value);
      const mediaWidth = parseFloat(mediaWidthSelect?.value);
      const copies = getSqftCopies();

      setProperty(propWidth, '');
      setProperty(propHeight, '');
      setProperty(propCopies, copies);

      if (!Number.isFinite(requested) || requested <= 0 || !mediaWidth) {
        setMessage('');
        setSecondary('');
        clearCalculatedState();
        setProperty(propCopies, copies);
        return;
      }

      const billableSquareFeet = Math.max(
        MINIMUM_SQUARE_FEET,
        Math.ceil(requested)
      );

      const requiredLengthInches = (billableSquareFeet * 144) / mediaWidth;

      if (mediaLength > 0 && requiredLengthInches > mediaLength) {
        setSecondary('');
        clearCalculatedState();
        setProperty(propCopies, copies);
        setMessage(
          `This amount exceeds the available printable media length of ${formatNumber(mediaLength)}″.`
        );
        return;
      }

      calculatorValid = true;
      currentSquareFeet = billableSquareFeet;

      setProperty(propSqft, billableSquareFeet);
      setProperty(propMedia, `${formatNumber(mediaWidth)}in`);
      setProperty(propCopies, copies);

      showResult(billableSquareFeet, mediaWidth);
      setMessage('');

      const approximateLengthFeet = requiredLengthInches / 12;
      setSecondary(`Approximate roll length: ${formatNumber(approximateLengthFeet)} ft`);

      publish(billableSquareFeet);
    }

    function populateMediaWidths() {
      if (!mediaWidthSelect) return;

      const previous = mediaWidthSelect.value;
      mediaWidthSelect.innerHTML = '';

      mediaWidths.forEach((width) => {
        const option = document.createElement('option');
        option.value = String(width);
        option.textContent = `${formatNumber(width)}″`;
        mediaWidthSelect.appendChild(option);
      });

      if (!mediaWidthSelect.options.length) {
        const option = document.createElement('option');
        option.value = '';
        option.textContent = 'No media sizes available';
        mediaWidthSelect.appendChild(option);
        return;
      }

      const stillExists = mediaWidths.some((width) => String(width) === String(previous));
      mediaWidthSelect.value = stillExists ? previous : String(mediaWidths[0]);
    }

    function setMode(mode) {
      activeMode = mode;
      calculatorValid = false;
      currentSquareFeet = 0;

      calc.querySelectorAll('[data-aw-tab]').forEach((button) => {
        const active = button.dataset.awTab === mode;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      calc.querySelectorAll('[data-aw-panel]').forEach((panel) => {
        const active = panel.dataset.awPanel === mode;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });

      setMessage('');
      setSecondary('');
      hideResult();

      if (mode === 'dimensions') {
        recalculateDimensions();
      } else {
        populateMediaWidths();
        updateCopyVisibility();
        recalculateSquareFootage();
      }

      publish(currentSquareFeet);
    }

    calc.querySelectorAll('[data-aw-tab]').forEach((button) => {
      button.addEventListener('click', () => setMode(button.dataset.awTab));
    });

    widthInput?.addEventListener('input', recalculateDimensions);
    heightInput?.addEventListener('input', recalculateDimensions);
    sqftInput?.addEventListener('input', recalculateSquareFootage);
    mediaWidthSelect?.addEventListener('change', recalculateSquareFootage);

    [
      singleRadio,
      fillRadio,
      quantityRadio,
      sqftSingleRadio,
      sqftQuantityRadio
    ].forEach((radio) => {
      radio?.addEventListener('change', () => {
        updateCopyVisibility();
        if (activeMode === 'dimensions') recalculateDimensions();
        else recalculateSquareFootage();
      });
    });

    quantityInput?.addEventListener('input', () => {
      if (quantityRadio?.checked && quantityInput.value !== '') recalculateDimensions();
    });

    quantityInput?.addEventListener('change', () => {
      normalizeQuantity(quantityInput);
      if (quantityRadio?.checked) recalculateDimensions();
    });

    sqftQuantityInput?.addEventListener('input', () => {
      if (sqftQuantityRadio?.checked && sqftQuantityInput.value !== '') recalculateSquareFootage();
    });

    sqftQuantityInput?.addEventListener('change', () => {
      normalizeQuantity(sqftQuantityInput);
      if (sqftQuantityRadio?.checked) recalculateSquareFootage();
    });

    calc.addEventListener('click', (event) => {
      const button = event.target.closest('[data-aw-copy-step]');
      if (!button || !calc.contains(button)) return;

      const stepper = button.closest('.aw-calc__stepper');
      const input = stepper?.querySelector(
        '[data-aw-copy-quantity], [data-aw-sqft-copy-quantity]'
      );
      if (!input) return;

      const current = getNumericQuantity(input);
      const delta = parseInt(button.dataset.awCopyStep, 10) || 0;
      input.value = Math.max(2, current + delta);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    document.addEventListener('change', (event) => {
      if (!event.target.matches('input[name="id"], select[name="id"]')) return;

      window.setTimeout(() => {
        if (activeMode === 'dimensions') recalculateDimensions();
        else recalculateSquareFootage();
      }, 50);
    });

    document.addEventListener('variant:change', () => {
      window.setTimeout(() => {
        if (activeMode === 'dimensions') recalculateDimensions();
        else recalculateSquareFootage();
      }, 50);
    });

    document.addEventListener('alwan:variant:change', () => {
      if (activeMode === 'dimensions') recalculateDimensions();
      else recalculateSquareFootage();
    });

    populateMediaWidths();
    updateCopyVisibility();
    setMode('dimensions');

    window.AlwanMediaCalculator = window.AlwanMediaCalculator || {};
    window.AlwanMediaCalculator.recalculate = () => {
      if (activeMode === 'dimensions') recalculateDimensions();
      else recalculateSquareFootage();
    };
    window.AlwanMediaCalculator.isValid = () => calculatorValid;
    window.AlwanMediaCalculator.getSquareFeet = () => currentSquareFeet;
    window.AlwanMediaCalculator.getMode = () => activeMode;
  }

  function initAll() {
    document.querySelectorAll('[data-aw-calc]').forEach(initCalculator);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})(window, document);
